import React, { useState, useEffect, useRef } from "react";
import { Button, Box } from "@mui/material";
import dayjs from "dayjs";
import { Add } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AxiosInstance from "../../services/networkServices";
import useDebounce from "../ManageUsers/useDebounce";
import PageLayout from "../../components/PageLayout";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/common";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import { usePermissions } from "../../hooks/usePermissions";
import { uploadImage, getUploadedMediaRecord } from "../../http/uploads";
import { appendLibrarySticker } from "../../components/ThemeStickerPicker";
import { validateFileExtension } from "../../utils";
import {
  createStickerUploadHandler,
  rememberUploadedSticker,
  syncStickerListRef,
} from "../../utils/stickerUploads";
import useStyles from "./styles";
import {
  isValidMediaId,
  getChallengeBackgroundUrl,
  MAX_STICKERS,
  isStickerEligibleCategory,
  isFeedGroupSizeEligibleCategory,
  normalizeStickers,
} from "./helpers";
import { normalizeFeedGroupSize } from "../../components/FormElements/FeedGroupSizeField";
import ChallengeFilters from "./ChallengeFilters";
import ChallengeTable from "./ChallengeTable";
import ChallengeFormModal from "./ChallengeFormModal";
import ChallengeViewModal from "./ChallengeViewModal";
import {
  alternativeNameOverLimit,
  alternativeNamesFromRecord,
  alternativeNamesPayload,
  emptyAlternativeNames,
} from "../../components/FormElements/alternativeLanguages";

const ManageChallenges = () => {
  const classes = useStyles();
  const [challenges, setChallenges] = useState([]);
  const [challengeTitle, setChallengeTitle] = useState("");
  const [alternativeNames, setAlternativeNames] = useState(emptyAlternativeNames);
  const [challengeCategory, setChallengeCategory] = useState("");
  const [challengeStartDate, setChallengeStartDate] = useState(null);
  const [challengeEndDate, setChallengeEndDate] = useState(null);
  // Schedule not decided yet — saved without dates, hidden from users
  const [challengeDateTbd, setChallengeDateTbd] = useState(false);
  const [challengeIsPromoted, setChallengeIsPromoted] = useState(false);
  const [challengeFeedGroupSize, setChallengeFeedGroupSize] = useState(null);
  const [logoId, setLogoId] = useState("");
  const [backgroundFileData, setBackgroundFileData] = useState(null);
  const [stickers, setStickers] = useState([]);
  const [stickersDirty, setStickersDirty] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [totalChallenges, setTotalChallenges] = useState(0);
  const [viewDetails, setViewDetails] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const dispatch = useDispatch();
  const stickersRef = useRef([]);
  syncStickerListRef(stickersRef, stickers);
  const editingChallengeRef = useRef(null);
  editingChallengeRef.current = editingChallenge;
  const stickerUploadRef = useRef(null);
  if (!stickerUploadRef.current) {
    stickerUploadRef.current = createStickerUploadHandler({
      max: MAX_STICKERS,
      getCurrent: () => stickersRef.current.length,
      folder: "challenge-stickers",
      metadata: () => ({
        type: "sticker",
        ...(editingChallengeRef.current?._id && {
          challengeId: editingChallengeRef.current._id,
        }),
      }),
      onUploaded: (media) => {
        setStickers(rememberUploadedSticker(stickersRef, media, MAX_STICKERS));
        setStickersDirty(true);
      },
      dispatch,
      setLoading,
    });
  }
  const limit = 30;
  const { isAdmin, canViewActivity } = usePermissions();

  // Sort and Filter
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("startDate");
  const [sortOrder, setSortOrder] = useState(-1); // default is -1 for desc, 1 for asc

  useEffect(() => {
    fetchChallenges();
    fetchCategories();
  }, [page, debouncedSearch, categoryFilter, statusFilter, sortBy, sortOrder]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [categoryFilter, sortBy, sortOrder, statusFilter]);

  // fetchHandlers
  const fetchCategories = async () => {
    let payload = {
      page: page + 1,
      limit: 100,
    };
    try {
      const response = await AxiosInstance.post("/category/list", payload);
      if (response.data.status) {
        setCategories(response.data.data.list);
      } else {
        console.error("Failed to fetch categories:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchChallenges = async () => {
    let payload = {
      page: page + 1,
      limit: limit,
    };

    // Add search filter
    if (debouncedSearch) payload = { ...payload, search: debouncedSearch };

    // Add category filter
    if (categoryFilter.length === 1) {
      payload = { ...payload, categoryId: categoryFilter[0] };
    } else if (categoryFilter.length > 1) {
      payload = { ...payload, categoryIds: categoryFilter };
    }

    // Add date-based filtering for status
    if (statusFilter) {
      const now = new Date();
      if (statusFilter === "Scheduled") {
        payload.startDate = new Date();
      } else if (statusFilter === "In Progress") {
        payload.active = true;
      } else if (statusFilter === "Ended") {
        payload.endDate = new Date();
      } else if (statusFilter === "Upcoming") {
        payload.upcoming = true;
      }
    }

    // Add sorting parameters
    if (sortBy) {
      payload.sortBy = sortBy;
      payload.sortOrder = sortOrder;
    }

    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "/category/challenge/list",
        payload
      );
      if (response.data.status) {
        let list = response.data.data.list.slice();
        if (statusFilter === "Upcoming") {
          const now = new Date();
          // Challenges without dates are TBD and count as upcoming
          list = list.filter((c) => !c.endDate || new Date(c.endDate) >= now);
        }
        list.sort((a, b) => {
          const dateA = new Date(a[sortBy] || 0).getTime();
          const dateB = new Date(b[sortBy] || 0).getTime();
          if (dateA !== dateB) return (dateA - dateB) * sortOrder;
          const nameA = (a.category?.name || "").toLowerCase();
          const nameB = (b.category?.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChallenges(list);
        setTotalChallenges(
          statusFilter === "Upcoming" ? list.length : response.data.data.resultCount
        );
      } else {
        console.error("Failed to fetch challenges:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  // dialog handlers
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  const resetBackgroundImageState = () => {
    setBackgroundFileData(null);
    setEditingChallenge(null);
  };
  const handleCloseDialog = () => {
    setChallengeTitle("");
    setAlternativeNames(emptyAlternativeNames());
    setChallengeCategory("");
    setChallengeStartDate("");
    setChallengeEndDate("");
    setChallengeDateTbd(false);
    setChallengeIsPromoted(false);
    setChallengeFeedGroupSize(null);
    setLogoId("");
    setBackgroundFileData(null);
    setStickers([]);
    setStickersDirty(false);
    setEditingChallenge(null);
    setIsEditMode(false);
    setEditIndex(null);
    setOpenDialog(false);
  };

  const handleBackgroundFileUpload = async (options) => {
    const { file, onSuccess, onError } = options;

    const validFormats = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    const isValidMimeType = file.type && validFormats.includes(file.type);
    const isValidExtension = validateFileExtension(file.name, "");

    if (!isValidMimeType && !isValidExtension) {
      dispatch(
        showToast({
          color: "error",
          msg: "Invalid file format. Please upload a JPG, JPEG, or PNG image.",
        })
      );
      onError(new Error("Invalid file format"));
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        showToast({
          color: "error",
          msg: "File size exceeds 5MB limit. Please upload a smaller file.",
        })
      );
      onError(new Error("File size too large"));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "challenges");
      setLoading(true);
      const res = await uploadImage(formData);
      const media = getUploadedMediaRecord(res);
      setBackgroundFileData(media);
      dispatch(
        showToast({
          color: "success",
          msg: "Background image uploaded successfully.",
        })
      );
      onSuccess(media);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to upload background image. Please try again.",
        })
      );
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStickerUpload = stickerUploadRef.current;

  const handleAddLibrarySticker = (sticker) => {
    setStickers((prev) => appendLibrarySticker(prev, sticker, MAX_STICKERS));
    setStickersDirty(true);
  };

  const handleRemoveSticker = (index) => {
    setStickers((prev) => prev.filter((_, i) => i !== index));
    setStickersDirty(true);
  };

  const handleClearStickers = () => {
    setStickers([]);
    setStickersDirty(true);
  };

  // change handlers
  const handleCategoryChange = (event) => {
    const selectedCategoryId = event.target.value;
    const selectedCategory = categories.find(
      (cat) => cat._id === selectedCategoryId
    );

    if (selectedCategory) {
      console.log("Selected category:", selectedCategory);
      setChallengeCategory(selectedCategoryId);
      setLogoId(
        isValidMediaId(selectedCategory.logoId) ? selectedCategory.logoId : ""
      );
      if (!isStickerEligibleCategory(selectedCategory) && stickers.length) {
        setStickers([]);
        setStickersDirty(true);
      }
    } else {
      console.error("Category not found:", selectedCategoryId);
    }
  };
  const handleAddChallenge = async () => {
    try {
      setLoading(true);
      if (!challengeTitle.trim()) {
        dispatch(
          showToast({ color: "error", msg: "Challenge name is required." })
        );
        return;
      }
      if (challengeTitle?.trim()?.length < 1) {
        dispatch(
          showToast({
            color: "error",
            msg: "Challenge name must be at least 1 character long.",
          })
        );
        return;
      }
      if (challengeTitle?.trim()?.length > 50) {
        dispatch(
          showToast({
            color: "error",
            msg: "Challenge name must not exceed 50 characters.",
          })
        );
        return;
      }
      const longAlternative = alternativeNameOverLimit(alternativeNames, 50);
      if (longAlternative) {
        dispatch(
          showToast({
            color: "error",
            msg: `${longAlternative.label} name must not exceed 50 characters.`,
          })
        );
        return;
      }
      if (!challengeCategory.trim()) {
        dispatch(showToast({ color: "error", msg: "Category is required." }));
        return;
      }
      // Dates are required unless "Schedule not decided yet (TBD)" is
      // checked; a TBD challenge is saved without dates and stays hidden
      // from users until it's scheduled.
      if (!challengeDateTbd && (!challengeStartDate || !challengeEndDate)) {
        dispatch(
          showToast({
            color: "error",
            msg: "Set both start and end dates, or check \"Schedule not decided yet (TBD)\".",
          })
        );
        return;
      }

      const newChallenge = {
        title: challengeTitle,
        localizedTitles: alternativeNamesPayload(alternativeNames),
        categoryId: challengeCategory,
      };

      if (!challengeDateTbd) {
        const startDateObj = new Date(challengeStartDate);
        const endDateObj = new Date(challengeEndDate);
        if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
          dispatch(showToast({ color: "error", msg: "Invalid dates" }));
          return;
        }
        newChallenge.startDate = startDateObj.toISOString();
        newChallenge.endDate = endDateObj.toISOString();
      }

      if (isValidMediaId(backgroundFileData?._id)) {
        newChallenge.backgroundImageId = backgroundFileData._id;
      }

      if (stickersAllowed) {
        const stickerIds = stickers
          .map((sticker) => sticker.mediaId)
          .filter(isValidMediaId);
        if (stickerIds.length) {
          newChallenge.stickerIds = stickerIds;
        }
      }

      if (isFeedGroupSizeEligibleCategory(selectedCategory)) {
        newChallenge.feedGroupSize = normalizeFeedGroupSize(challengeFeedGroupSize);
      }

      const response = await AxiosInstance.post(
        "/category/challenge",
        newChallenge
      );

      if (response.status === 200) {
        fetchChallenges();
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Error adding challenge:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to create challenge. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChallenge = async () => {
    try {
      if (!challengeTitle.trim()) {
        dispatch(
          showToast({ color: "error", msg: "Challenge name is required" })
        );
        return;
      }
      if (challengeTitle?.trim()?.length < 1) {
        dispatch(
          showToast({
            color: "error",
            msg: "Challenge name must be at least 1 character long.",
          })
        );
        return;
      }
      if (challengeTitle?.trim()?.length > 50) {
        dispatch(
          showToast({
            color: "error",
            msg: "Challenge name must not exceed 50 characters.",
          })
        );
        return;
      }
      const longAlternative = alternativeNameOverLimit(alternativeNames, 50);
      if (longAlternative) {
        dispatch(
          showToast({
            color: "error",
            msg: `${longAlternative.label} name must not exceed 50 characters.`,
          })
        );
        return;
      }
      if (!challenges[editIndex] || !challenges[editIndex]._id) {
        dispatch(
          showToast({
            color: "error",
            msg: "Challenge ID (_id) is missing or invalid.",
          })
        );
        return;
      }

      const challengeId = challenges[editIndex]._id;
      if (!/^[0-9a-fA-F]{24}$/.test(challengeId)) {
        dispatch(
          showToast({ color: "error", msg: "Invalid challenge ID format." })
        );
        return;
      }
      console.log(challenges[editIndex]);
      console.log(logoId);

      // Dates are required unless "Schedule not decided yet (TBD)" is checked
      if (!challengeDateTbd && (!challengeStartDate || !challengeEndDate)) {
        dispatch(
          showToast({
            color: "error",
            msg: "Set both start and end dates, or check \"Schedule not decided yet (TBD)\".",
          })
        );
        return;
      }

      let formattedStartDate = null;
      let formattedEndDate = null;
      if (!challengeDateTbd) {
        const startDateObj = dayjs(challengeStartDate);
        const endDateObj = dayjs(challengeEndDate);
        if (!startDateObj.isValid() || !endDateObj.isValid()) {
          dispatch(showToast({ color: "error", msg: "Invalid dates." }));
          return;
        }
        formattedStartDate = startDateObj.toISOString();
        formattedEndDate = endDateObj.toISOString();
      }
      setLoading(true);

      let updatedChallenge = {};
      if (formattedEndDate) {
        updatedChallenge.endDate = formattedEndDate;
      } else if (challenges[editIndex].startDate || challenges[editIndex].endDate) {
        // Dates were cleared — un-schedule the challenge on the server
        updatedChallenge.startDate = null;
        updatedChallenge.endDate = null;
      }
      // Only send isPromoted when the admin actually toggled it; echoing the
      // stored value re-triggers the backend's promotion validation on every
      // save (rejecting edits of promoted challenges that already ended).
      if (
        Boolean(challengeIsPromoted) !==
        Boolean(challenges[editIndex].isPromoted)
      ) {
        updatedChallenge.isPromoted = Boolean(challengeIsPromoted);
      }
      if (isValidMediaId(logoId)) {
        updatedChallenge.logoId = logoId;
      }
      if (isValidMediaId(backgroundFileData?._id)) {
        updatedChallenge.backgroundImageId = backgroundFileData._id;
      } else {
        const original = challenges[editIndex];
        const hadBackground =
          Boolean(original?.backgroundImageId) ||
          Boolean(getChallengeBackgroundUrl(original));
        // User clicked X to remove — clear on the server
        if (hadBackground && !backgroundFileData) {
          updatedChallenge.backgroundImageId = null;
        }
      }

      // Stickers are replaced as a whole set, so only send them when touched
      if (
        isFeedGroupSizeEligibleCategory(selectedCategory) &&
        normalizeFeedGroupSize(challengeFeedGroupSize) !==
          normalizeFeedGroupSize(challenges[editIndex].feedGroupSize)
      ) {
        updatedChallenge.feedGroupSize = normalizeFeedGroupSize(challengeFeedGroupSize);
      }

      if (stickersDirty) {
        const stickerIds = stickers.map((sticker) => sticker.mediaId);
        if (stickerIds.some((id) => !isValidMediaId(id))) {
          dispatch(
            showToast({
              color: "error",
              msg: "Some stickers can't be kept automatically. Remove all stickers and re-upload the ones you want.",
            })
          );
          return;
        }
        updatedChallenge.stickerIds = stickerIds;
      }
      if (challenges[editIndex].startDate !== challengeTitle) {
        updatedChallenge.title = challengeTitle;
      }
      updatedChallenge.localizedTitles = alternativeNamesPayload(alternativeNames);
      if (
        formattedStartDate &&
        challenges[editIndex].startDate !== formattedStartDate
      ) {
        updatedChallenge.startDate = formattedStartDate;
      }

      const response = await AxiosInstance.put(
        `/category/challenge/${challengeId}`,
        updatedChallenge
      );

      if (response.status === 200) {
        await fetchChallenges();
        handleCloseDialog();
      } else {
        dispatch(
          showToast({ color: "error", msg: "Failed to update challenge" })
        );
        throw new Error("Failed to update challenge");
      }
    } catch (error) {
      console.error("Error updating challenge:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChallenge = async (challenge) => {
    if (!isAdmin) {
      dispatch(
        showToast({
          color: "error",
          msg: "You are not authorized to delete challenges.",
        })
      );
      return;
    }
    console.log(challenge);
    setDeleteIndex(challenges.findIndex((c) => c._id === challenge._id));
    setOpenDeleteModal(true);
  };
  const confirmDeleteChallenge = async () => {
    try {
      const deletedChallenge = challenges[deleteIndex];

      if (!deletedChallenge || !deletedChallenge._id) {
        throw new Error("Challenge ID is missing or invalid.");
      }

      const response = await AxiosInstance.delete(
        `/category/challenge/${deletedChallenge._id}`
      );

      if (response.status === 200) {
        await fetchChallenges();
        closeDeleteModalHandler();
      } else {
        dispatch(
          showToast({ color: "error", msg: "Failed to delete challenge" })
        );
      }
    } catch (error) {
      console.error("Error deleting challenge:", error);
      toast.error("Failed to delete challenge.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: { backgroundColor: "#f44336" },
      });
    }
  };
  const closeDeleteModalHandler = () => {
    setOpenDeleteModal(false);
  };

  const handleEditChallenge = (challenge) => {
    const backgroundUrl = getChallengeBackgroundUrl(challenge);
    setChallengeTitle(challenge.title);
    setAlternativeNames(alternativeNamesFromRecord(challenge));
    setChallengeCategory(challenge.categoryId || challenge.category?._id || "");
    // dayjs(undefined) silently becomes "now" — keep TBD challenges empty
    setChallengeStartDate(challenge.startDate ? dayjs(challenge.startDate) : null);
    setChallengeEndDate(challenge.endDate ? dayjs(challenge.endDate) : null);
    setChallengeDateTbd(!challenge.startDate && !challenge.endDate);
    setChallengeIsPromoted(challenge.isPromoted);
    setChallengeFeedGroupSize(normalizeFeedGroupSize(challenge.feedGroupSize));
    setLogoId(challenge.logoId);
    setEditingChallenge(challenge);
    setBackgroundFileData(
      backgroundUrl || challenge.backgroundImageId
        ? {
            url: backgroundUrl,
            _id: challenge.backgroundImageId,
          }
        : null
    );
    setStickers(normalizeStickers(challenge));
    setStickersDirty(false);
    setIsEditMode(true);
    setEditIndex(challenges.findIndex((c) => c._id === challenge._id));
    handleOpenDialog();
  };

  const handleViewChallenge = (challenge) => {
    setViewDetails(challenge);
    setOpenViewDialog(true);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleCategoryFilterChange = (event) => {
    const value = event.target.value;
    setCategoryFilter(typeof value === "string" ? value.split(",") : value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    if (value === "Upcoming") {
      setSortBy("startDate");
      setSortOrder(1);
    }
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prevSort => prevSort === 1 ? -1 : 1);
    } else {
      setSortBy(field);
      setSortOrder(-1);
    }
    setPage(0);
  };

  // Clear filters
  const clearFilters = () => {
    setCategoryFilter([]);
    setStatusFilter("");
    setSortBy("startDate");
    setSortOrder(-1);
    setSearch("");
    setPage(0);
  };

  const challengeBackgroundPreviewUrl =
    backgroundFileData?.url || getChallengeBackgroundUrl(editingChallenge);

  const selectedCategory =
    categories.find((cat) => cat._id === challengeCategory) ||
    editingChallenge?.category;
  const stickersAllowed = isStickerEligibleCategory(selectedCategory);
  const feedGroupSizeAllowed = isFeedGroupSizeEligibleCategory(selectedCategory);

  return (
    <PageLayout
      title="Challenges"
      loading={loading}
      actions={
        <Button
          sx={{
            padding: {
              xs: "6px",
              sm: "6px 16px",
              md: "6px 16px",
              lg: "6px 16px",
              xl: "6px 16px",
            },
            backgroundColor: "#142C5C",
            color: "#fff",
            borderColor: "#0f2247",
            "&:hover": {
              backgroundColor: "#0f2247 !important",
            },
            borderRadius: "6px",
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "13px",
              lg: "14px",
              xl: "14px",
            },
            height: {
              xs: "35px",
              sm: "40px",
              md: "40px",
              lg: "40px",
              xl: "40px",
            },
          }}
          variant="contained"
          onClick={() => {
            setIsEditMode(false);
            resetBackgroundImageState();
            handleOpenDialog();
          }}
        >
          <Add /> Add Challenge
        </Button>
      }
    >
      <Box className={classes.root}>
        <ChallengeFilters
          categories={categories}
          categoryFilter={categoryFilter}
          handleCategoryFilterChange={handleCategoryFilterChange}
          statusFilter={statusFilter}
          handleStatusFilterChange={handleStatusFilterChange}
          search={search}
          handleSearchChange={handleSearchChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          handleSort={handleSort}
          clearFilters={clearFilters}
        />

        <ChallengeTable
          classes={classes}
          challenges={challenges}
          canViewActivity={canViewActivity}
          handleViewChallenge={handleViewChallenge}
          handleEditChallenge={handleEditChallenge}
          handleDeleteChallenge={handleDeleteChallenge}
          limit={limit}
          totalChallenges={totalChallenges}
          page={page}
          handlePageChange={handlePageChange}
        />

        <ChallengeFormModal
          open={openDialog}
          onClose={handleCloseDialog}
          classes={classes}
          isEditMode={isEditMode}
          isAdmin={isAdmin}
          challengeTitle={challengeTitle}
          setChallengeTitle={setChallengeTitle}
          alternativeNames={alternativeNames}
          setAlternativeNames={setAlternativeNames}
          challengeCategory={challengeCategory}
          categories={categories}
          handleCategoryChange={handleCategoryChange}
          challengeStartDate={challengeStartDate}
          setChallengeStartDate={setChallengeStartDate}
          challengeEndDate={challengeEndDate}
          setChallengeEndDate={setChallengeEndDate}
          challengeDateTbd={challengeDateTbd}
          setChallengeDateTbd={setChallengeDateTbd}
          challengeBackgroundPreviewUrl={challengeBackgroundPreviewUrl}
          setBackgroundFileData={setBackgroundFileData}
          editingChallenge={editingChallenge}
          setEditingChallenge={setEditingChallenge}
          handleBackgroundFileUpload={handleBackgroundFileUpload}
          stickersAllowed={stickersAllowed}
          feedGroupSizeAllowed={feedGroupSizeAllowed}
          challengeFeedGroupSize={challengeFeedGroupSize}
          setChallengeFeedGroupSize={setChallengeFeedGroupSize}
          stickers={stickers}
          handleStickerUpload={handleStickerUpload}
          handleAddLibrarySticker={handleAddLibrarySticker}
          handleRemoveSticker={handleRemoveSticker}
          handleClearStickers={handleClearStickers}
          challengeIsPromoted={challengeIsPromoted}
          setChallengeIsPromoted={setChallengeIsPromoted}
          editIndex={editIndex}
          challenges={challenges}
          handleDeleteChallenge={handleDeleteChallenge}
          handleUpdateChallenge={handleUpdateChallenge}
          handleAddChallenge={handleAddChallenge}
        />

        <ChallengeViewModal
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          viewDetails={viewDetails}
        />

        {openDeleteModal && (
          <ConfirmDeleteModal
            challenge={true}
            onAccept={confirmDeleteChallenge}
            onReject={closeDeleteModalHandler}
          />
        )}

        <ToastContainer />
      </Box>
    </PageLayout>
  );
};

export default ManageChallenges;
